import { createFileRoute, Outlet, redirect } from "@tanstack/react-router"

import PendingComponent from "@/globalComponents/atoms/PendingComponent"
import ErrorComponent from "@/globalComponents/organisms/ErrorComponent"
import { log } from "@/utilities/logging"
import welcomeTokenStorage from "@/utilities/WelcomeTokenStorage"

export const Route = createFileRoute("/qr/$qrLookupID")({
  beforeLoad: async ({ params, matches }) => {
    /**
     * Receipt and authenticated account pages are deep-linkable without a
     * welcome token: they theme off the path param and gate on Cognito auth.
     *
     * The account subtree is exempt from *requiring* a token, not from the
     * stale-QR check — `/qr/$qrLookupID/_authenticated` runs its own, and
     * resolves a mismatch the opposite way to this one: it keeps the token and
     * redirects to the *stored* QR, where this guard clears the token and
     * redirects to the *supplied* one.
     *
     * The URL is what makes the difference. Reaching `/qr/B/park/123` means
     * someone is standing at lot B, so the supplied QR is better evidence and
     * the old session is genuinely stale. An account URL is usually a bookmark
     * or a forwarded link carrying an unvalidated `qrLookupID` that can't mint
     * a token, so honoring it would clear a working session; there the stored
     * QR is the only known-good value.
     */
    const isExempt = matches.some(
      (m) =>
        m.routeId === "/qr/$qrLookupID/receipt/$receiptID" ||
        m.routeId.startsWith("/qr/$qrLookupID/_authenticated") ||
        m.routeId.startsWith("/qr/$qrLookupID/dev")
    )
    if (isExempt) return

    // Validate qrLookupID against stored session data
    const storedQrLookupID = welcomeTokenStorage.getQrLookupID()

    if (storedQrLookupID && storedQrLookupID !== params.qrLookupID) {
      // QR has changed - clear old session data and redirect to welcome with the new QR
      // This will generate a new token with the current QR lookup ID

      log({
        level: "log",
        flow: "misc",
        scope: "guard.qrLookupID",
        message: "QrLookup Changed",
        meta: {
          storedQrLookupID,
          suppliedQrLookupID: params.qrLookupID,
        },
      })

      welcomeTokenStorage.removeToken()

      throw redirect({
        to: "/qr/$qrLookupID/welcome",
        params: { qrLookupID: params.qrLookupID },
      })
    }
  },
  loader: ({ context }) => {
    context.setDocumentThemeMode("whiteLabel")
  },
  preload: false,
  pendingComponent: PendingComponent,
  // notFoundComponent: (opts) => <RootNotFound {...opts} />,
  errorComponent: (opts) => (
    <ErrorComponent {...opts} resolution="restartFromWelcome" />
  ),
  component: Outlet,
})
